import { formatCurrencyFromString } from '@/shared/utils';
import {
  Button,
  Dialog,
  Field,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { useEffect, useState } from 'react';

export type CustomDeductible = {
  percentage: string;
  value: string;
};

export function formatCustomDeductibleLabel(custom: CustomDeductible): string {
  const percentage = (custom.percentage ?? '').trim();
  const value = (custom.value ?? '').trim();
  if (!percentage && !value) return '';
  const numericValue = value.replace(/^R\$\s*/i, '').trim();
  if (!percentage) return value;
  if (!numericValue) return `${percentage}%`;
  return `${percentage}% dos prejuízos indenizáveis com mínimo de R$ ${numericValue}`;
}

type CustomizeDeductibleDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  defaultLabel?: string;
  currentValue?: CustomDeductible;
  onApply: (value: CustomDeductible) => void;
};

const sanitizePercentage = (raw: string): string => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  const trimmed = digits.replace(/^0+(?=\d)/, '');
  const numeric = Math.min(100, parseInt(trimmed, 10));
  return Number.isNaN(numeric) ? '' : String(numeric);
};

const sanitizeCurrency = (raw: string): string => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return formatCurrencyFromString(raw);
};

export function CustomizeDeductibleDialog({
  isOpen,
  onOpenChange,
  defaultLabel,
  currentValue,
  onApply,
}: CustomizeDeductibleDialogProps) {
  const [percentage, setPercentage] = useState(currentValue?.percentage ?? '');
  const [value, setValue] = useState(currentValue?.value ?? '');

  useEffect(() => {
    if (!isOpen) return;
    setPercentage(currentValue?.percentage ?? '');
    setValue(currentValue?.value ?? '');
  }, [isOpen, currentValue]);

  const isValid = percentage.trim().length > 0 && value.trim().length > 0;

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleApply = () => {
    if (!isValid) return;
    onApply({ percentage, value });
    onOpenChange(false);
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={details => onOpenChange(details.open)}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='mx-4 w-[calc(100%-2rem)] max-w-md sm:mx-0 sm:w-full'>
          <Dialog.Header>
            <Dialog.Title>Personalizar Franquia</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body className='flex flex-col gap-4'>
            {defaultLabel ? (
              <Text className='text-sm text-alba-gray-600'>
                Padrão da lista: {defaultLabel}
              </Text>
            ) : null}
            <div className='flex flex-col sm:flex-row items-stretch gap-4'>
              <Field.Root className='flex-1'>
                <Field.Label className='text-sm font-bold text-alba-black-500'>
                  Percentual
                </Field.Label>
                <Field.Input
                  type='text'
                  inputMode='numeric'
                  value={percentage ? `${percentage}%` : ''}
                  onChange={e => setPercentage(sanitizePercentage(e.target.value))}
                  placeholder='% 000'
                  className='w-full h-[39px] px-5 py-3 text-xs'
                />
              </Field.Root>
              <Field.Root className='flex-1'>
                <Field.Label className='text-sm font-bold text-alba-black-500'>
                  Franquia
                </Field.Label>
                <Field.Input
                  type='text'
                  inputMode='numeric'
                  value={value}
                  onChange={e => setValue(sanitizeCurrency(e.target.value))}
                  placeholder='R$ 000,00'
                  className='w-full h-[39px] px-5 py-3 text-xs'
                />
              </Field.Root>
            </div>
          </Dialog.Body>
          <Dialog.Footer className='flex justify-end gap-3'>
            <Button variant='ghost' size='md' onClick={handleCancel}>
              Cancelar
            </Button>
            <Button
              variant='primary'
              size='md'
              onClick={handleApply}
              disabled={!isValid}
            >
              Aplicar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
